import { useTags } from "sdk/command-palette/queries"

export const PAGE_ID = "tag-search"

// Global chord that opens the palette straight onto this subpage.
export const SHORTCUT = "f t"

// Root-level entry that opens the subpage. It lives here (not with the global
// navigation) so the whole capability — entry, results, and chord — ships and
// disappears together with this provider's registration.
const SEARCH_ENTRY_COMMAND = {
  id: "search-tags",
  label: "Search tags…",
  icon: "search",
  group: "Search",
  keywords: ["find", "tag", "label"],
  shortcut: SHORTCUT,
  onSelect: api => api.pushPage(PAGE_ID)
}

/**
 * Provider for tag search. At the root it offers the "Search tags…" entry that
 * opens the subpage. Unlike subscriber search, the whole tag list is small and
 * changes rarely, so we preload it once when the subpage opens and let cmdk
 * filter it client-side (the subpage sets `shouldFilter: true`). Selecting a tag
 * navigates to the subscribers list filtered by that tag.
 *
 * @type {import("../types").Provider}
 */
export function useTagSearchProvider({ activePageId }) {
  const active = activePageId === PAGE_ID

  // Preload the full list when the subpage is open; no debounce or query gating
  // because filtering happens on the client.
  const { data, isFetching } = useTags({ enabled: active })

  // Root: offer the entry point into the subpage.
  if (!activePageId) {
    return { commands: [SEARCH_ENTRY_COMMAND], isLoading: false }
  }

  // A different subpage owns the list.
  if (!active) return { commands: [], isLoading: false }

  const commands = (data?.tags ?? []).map(tag => ({
    id: `tag-${tag.id}`,
    label: tag.name,
    icon: "tag",
    group: "Tags",
    onSelect: api =>
      api.navigate(
        `/subscribers?subscribable_type=Tag&subscribable_ids=${tag.id}`
      )
  }))

  return { commands, isLoading: isFetching }
}

/**
 * Global chords contributed by this provider: `f t` opens the palette straight
 * onto the tag-search subpage.
 *
 * @returns {import("../types").Shortcut[]}
 */
export function getTagSearchShortcuts() {
  return [{ shortcut: SHORTCUT, onTrigger: api => api.pushPage(PAGE_ID) }]
}
